<template>
    <ModalDialogContainer class="confirmation-modal" ref="modalContainer" v-model:display="displayStatus">
        <div class="modal-dialog modal-md" role="document" @click="stopPropagationEvent">
            <div class="modal-content">
                <div class="modal-header">
                    <div class="modal-title">{{ title }} </div>
                    <button type="button" class="btn-close" :title="$t('Close')" @click="close"></button>
                </div>
                <div class="modal-body">
                    <p>{{ message }}</p>
                    <div v-if="danger" class="form-check form-switch">
                        <input class="form-check-input cursor-pointer" type="checkbox" role="switch"
                            v-model="confirmed" />
                        <label>{{ dangerText || $t("I understand that this is a destructive action with non-reversible effects.")
                            }}</label>
                    </div>
                    <div v-if="showAgain" class="dont-show-again">
                        <CustomCheck ref="showAgainCheck" class="dont-show-again-check" @click="showModal = !showModal"></CustomCheck>
                        <label @click="toggleDontShowAgain">{{ $t("Do not show this message again.") }}</label>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-primary" @click="confirm" v-if="!danger && !simpleDelete">
                        <i :class="processedIcon"></i> {{ buttonText || $t("Confirm") }}
                    </button>
                    <button type="button" class="btn btn-primary btn-delete" @click="confirm" :disabled="!confirmed" v-if="danger">
                        <i class="fa-solid" :class="processedIcon || 'fa-trash-can'"></i> {{ buttonText || $t("Delete") }}
                    </button>
                    <button type="button" class="btn btn-primary btn-delete" @click="confirm" v-if="simpleDelete">
                        <i class="fa-solid fa-trash-can"></i> {{ $t("Delete") }}
                    </button>
                    <button type="button" class="btn btn-cancel" @click="close">
                        {{ $t("Cancel") }}
                    </button>
                </div>
            </div>
        </div>
    </ModalDialogContainer>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useVModel } from "@/utils/v-model";
import { getIconClass } from "@/utils/icon-checker";
import CustomCheck from "@/components/utils/CustomizableCheck.vue";
import { saveIntoLocalStorage } from "@/utils/local-storage";

export default defineComponent({
    name: "ConfirmationModal",
    emits: ["update:display", "confirm"],
    components: {
        CustomCheck,
    },
    props: {
        display: Boolean,
        title: String,
        message: String,
        danger: Boolean,
        simpleDelete: Boolean,
        showAgain: Boolean,
        buttonText: String,
        dangerText: String,
        icon: {
            type: String,
            default: "fa-solid fa-check",
        },
    },
    setup(props) {
        return {
            displayStatus: useVModel(props, "display"),
        };
    },
    data: function () {
        return {
            confirmed: false,
            showModal: true,
            processedIcon: getIconClass(this.icon),
        };
    },
    methods: {
        close: function () {
            this.displayStatus = false;
        },

        escapeToClose: function (event) {
            if (event.key === "Escape") {
                this.close();
            }
        },

        stopPropagationEvent: function (e) {
            e.stopPropagation();
        },

        toggleDontShowAgain: function () {
            const check = this.$refs.showAgainCheck as { toggleCheck?: () => void };
            check?.toggleCheck?.();
            this.showModal = !this.showModal;
        },

        confirm: function () {
            if (this.showAgain && !this.showModal) {
                saveIntoLocalStorage("showConfirmModal", false);
            }
            this.$emit("confirm");
            this.close();
        },
    },
    mounted: function () {
        if (this.display) {
            this.$autoFocus();
        }
    },
    watch: {
        display: function () {
            if (this.display) {
                this.$autoFocus();
            }
        },
    },
});
</script>

<style scoped>
.dont-show-again {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0;
    margin: 0;
}

.dont-show-again label {
    margin: 0;
    padding: 0;
    line-height: 1.2;
    cursor: pointer;
}

.dont-show-again :deep(.dont-show-again-check),
.dont-show-again :deep(.dont-show-again-check i) {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    line-height: 1;
}
</style>
